import { type CSSProperties, useId } from "react";

import { formatBucketTime, formatCompactCount } from "./chart-format";
import { catmullRomPath, linearPath } from "./chart-geometry";
import { ChartHoverTooltip, useChartHover } from "./chart-hover-tooltip";
import { ChartLegend } from "./chart-legend";
import type { BucketFormatter, ChartSeries, ValueFormatter } from "./chart-types";
import { cn } from "./cn";
import "./chart-line-area.css";

export interface LineAreaChartProps {
  title: string;
  /** Shared x-axis buckets. May be ISO minute/hour strings (the default
   *  formatBucket assumes this) or arbitrary category labels when a custom
   *  `formatBucket` is supplied. */
  buckets: string[];
  /** 1..8 series, index-aligned to `buckets`. */
  series: ChartSeries[];
  /** From useCategoricalColors(series.map(s => s.key)), or explicit overrides. */
  colors: Map<string, string>;
  /** "area" (gradient fill + line) is only meaningful for a single series -
   *  overlapping/stacked area fills read as ORDERING, not magnitude, past one
   *  series (a dataviz anti-pattern), so requesting "area" with 2+ series logs
   *  a warning and renders as "line" instead. Defaults to "area" for exactly
   *  one series, "line" for 2+. */
  variant?: "area" | "line";
  /** Formats an x-axis bucket for the axis + hover tooltip. Default: an ISO
   *  minute/hour string -> a clock-time label ("14:32"). */
  formatBucket?: BucketFormatter;
  /** Formats a y value for the tooltip, peak label, and default total. */
  formatValue?: ValueFormatter;
  /** The caption's total/window figure, e.g. "1,204 in 24h" — supply this
   *  whenever summing `series` would be wrong (see BarChart's `total`
   *  override for the same escape hatch). Defaults to the sum of every
   *  series' values, formatted with `formatValue`. */
  totalLabel?: string;
  /** Override the auto-built <svg role="img"> accessible name. */
  ariaLabel?: string;
  /** Lower-profile variant for a demoted/secondary panel. */
  compact?: boolean;
  /** Opens the drilldown behind this chart's total. Renders the caption total
   *  as a real button when present, a plain span otherwise. */
  onOpen?: () => void;
  /** Opt-in floating tooltip that follows the cursor and shows every
   *  series' value for the hovered bucket at once (mouse only). Defaults to
   *  `false` — see BarChart's `hoverTooltip` doc for why this stays opt-in
   *  rather than default-on. The existing invisible per-bucket `<rect>` hit
   *  columns (and their `<title>`) are unchanged either way: they remain the
   *  accessible-name path for a mouse+AT combination. */
  hoverTooltip?: boolean;
  /** The right-hand axis label. Defaults to "now", which is correct only for a
   *  rolling live window. For a historical or reader-chosen window (every BI
   *  chart), pass the last bucket's own formatted label, or the chart claims
   *  the final bucket is the present. */
  axisEndLabel?: string;
  /** Draw the line as a Catmull-Rom spline instead of straight segments.
   *  Off by default: a spline wanders between measured points and invents
   *  local maxima on dates that have none (see `linearPath`). Opt in only when
   *  the series really is a smooth underlying signal. */
  smooth?: boolean;
  /** Caption under the x scale. Adds 12 units of bottom room when set. */
  xAxisTitle?: string;
  /** Caption for the value scale, drawn top-left in the existing head room
   *  rather than rotated down the side — the same placement the artifact page
   *  this was built to match uses, and the one that needs no left padding and
   *  so leaves the bucket/hover mapping untouched. */
  yAxisTitle?: string;
  className?: string;
}

/**
 * Gradient area chart (single series) or multi-line chart (2+ series): a
 * straight-segment line (Catmull-Rom only with `smooth`), soft under-fill on
 * the single-series area variant, recessive gridlines, a "now" dot at the
 * latest point per series, and the current value direct-labeled at the head
 * of the line when there is exactly one series (a label per line gets
 * unreadable past one). Per-bucket hover tooltips ride invisible full-height
 * hit columns. A legend renders automatically for 2+ series.
 */
export function LineAreaChart({
  title,
  buckets,
  series,
  colors,
  variant,
  formatBucket = formatBucketTime,
  axisEndLabel = "now",
  smooth = false,
  formatValue = formatCompactCount,
  totalLabel,
  ariaLabel,
  compact,
  onOpen,
  hoverTooltip = false,
  xAxisTitle,
  yAxisTitle,
  className,
}: LineAreaChartProps) {
  const gradId = useId();
  let resolvedVariant = variant ?? (series.length === 1 ? "area" : "line");
  if (resolvedVariant === "area" && series.length > 1) {
    console.warn(
      'LineAreaChart: variant="area" only renders a single series (overlapping area fills read as ordering, not magnitude); falling back to "line".',
    );
    resolvedVariant = "line";
  }

  const W = 480;
  const H = compact ? 96 : 150 + (xAxisTitle ? 12 : 0);
  const PAD_TOP = 26;
  const PAD_BOTTOM = 18 + (xAxisTitle ? 12 : 0);
  const plotH = H - PAD_TOP - PAD_BOTTOM;
  const baseY = H - PAD_BOTTOM;
  // The domain spans zero, not starts at it: a DIFFERENCE series (Mira minus
  // Job Post) goes negative, and a 0-anchored domain drew those days as nothing.
  const values = series.flatMap((s) => s.values);
  const hi = Math.max(...values, 0);
  const lo = Math.min(...values, 0);
  const span = Math.max(hi - lo, 1);
  const n = Math.max(buckets.length - 1, 1);

  const x = (i: number) => (i / n) * W;
  const y = (v: number) => baseY - ((v - lo) / span) * plotH;
  /** Where value 0 sits — the baseline, and the area fill's foot. */
  const zeroY = y(0);

  const drawn = series.map((s) => {
    const pts = s.values.map((v, i) => [x(i), y(v)] as const);
    const last = s.values[s.values.length - 1] ?? 0;
    const lastPt = pts[pts.length - 1] ?? ([W, zeroY] as const);
    return {
      series: s,
      pts,
      path: smooth ? catmullRomPath(pts, zeroY) : linearPath(pts),
      last,
      lastPt,
      color: colors.get(s.key) ?? "var(--rc-chart-other)",
    };
  });
  const single = drawn.length === 1 ? drawn[0] : null;
  const colW = W / Math.max(buckets.length, 1);

  const total =
    totalLabel ??
    formatValue(series.reduce((sum, s) => sum + s.values.reduce((a, b) => a + b, 0), 0));

  const resolvedAriaLabel =
    ariaLabel ??
    (single
      ? `${title}, ${total}, now ${formatValue(single.last)}`
      : `${title}, ${total}. Series: ${series.map((s) => s.label).join(", ")}.`);

  // Points are evenly spaced edge-to-edge ("nearest" mode) rather than
  // stepped regions: a line chart's mark IS the data point, so hovering
  // anywhere nearest a point shows every series' value there at once (same
  // multi-series-at-a-glance rule BarChart applies via its own "step" mode).
  const hover = useChartHover(buckets.length, "nearest", hoverTooltip);
  const hoveredIndex = hover.index;
  const hoverTooltipNode =
    hoveredIndex === null ? null : (
      <ChartHoverTooltip anchor={hover.anchor}>
        <span className="rc-chart-hover-tooltip__bucket">
          {formatBucket(buckets[hoveredIndex] ?? "", hoveredIndex)}
        </span>
        {drawn.map((d) => (
          <span key={d.series.key} className="rc-chart-hover-tooltip__row">
            <span
              className="rc-chart-hover-tooltip__swatch"
              aria-hidden="true"
              style={{ background: d.color }}
            />
            <span className="rc-chart-hover-tooltip__label">{d.series.label}</span>
            <span className="rc-chart-hover-tooltip__value">
              {formatValue(d.series.values[hoveredIndex] ?? 0)}
            </span>
          </span>
        ))}
      </ChartHoverTooltip>
    );

  return (
    <figure
      className={cn("rc-chart", className)}
      style={single ? ({ "--rc-chart-accent": single.color } as CSSProperties) : undefined}
    >
      <figcaption className="rc-chart__caption">
        {single ? <span className="rc-chart__swatch" aria-hidden="true" /> : null}
        <span className="rc-chart__title">{title}</span>
        {onOpen ? (
          <button
            type="button"
            className="rc-chart__total"
            onClick={onOpen}
            title={`See the rows behind ${title.toLowerCase()}`}
          >
            {total}
          </button>
        ) : (
          <span className="rc-chart__total">{total}</span>
        )}
      </figcaption>
      {drawn.length > 1 ? <ChartLegend series={series} colors={colors} /> : null}
      <svg
        className="rc-chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={resolvedAriaLabel}
        {...hover.handlers}
      >
          {yAxisTitle ? (
            <text className="rc-chart__axis-title" x={2} y={9}>
              {yAxisTitle}
            </text>
          ) : null}
          {xAxisTitle ? (
            <text
              className="rc-chart__axis-title"
              x={W / 2}
              y={H - 3}
              textAnchor="middle"
            >
              {xAxisTitle}
            </text>
          ) : null}
        {single && resolvedVariant === "area" ? (
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={single.color} stopOpacity={0.32} />
              <stop offset="100%" stopColor={single.color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
        ) : null}
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            className="rc-chart__grid"
            x1={0}
            y1={baseY - plotH * f}
            x2={W}
            y2={baseY - plotH * f}
          />
        ))}
        {single && resolvedVariant === "area" ? (
          <path d={`${single.path} L${W},${zeroY} L0,${zeroY} Z`} fill={`url(#${gradId})`} />
        ) : null}
        {drawn.map((d) => (
          <path
            key={d.series.key}
            d={d.path}
            className="rc-chart__line"
            style={{ "--rc-chart-accent": d.color } as CSSProperties}
          />
        ))}
        <line className="rc-chart__baseline" x1={0} y1={zeroY} x2={W} y2={zeroY} />
        {/* Invisible full-height hover columns: a hit target far bigger than
            the line itself, one per bucket, listing every series' value. */}
        {buckets.map((bucket, i) => {
          const bucketLabel = formatBucket(bucket, i);
          const tooltip = single
            ? `${bucketLabel}: ${formatValue(single.series.values[i] ?? 0)}`
            : `${bucketLabel}\n${drawn
                .map((d) => `${d.series.label}: ${formatValue(d.series.values[i] ?? 0)}`)
                .join("\n")}`;
          return (
            <rect
              key={bucket || i}
              className="rc-chart__hit"
              x={x(i) - colW / 2}
              y={0}
              width={colW}
              height={H}
            >
              <title>{tooltip}</title>
            </rect>
          );
        })}
        {single ? (
          <circle
            className="rc-chart__now-halo"
            cx={single.lastPt[0]}
            cy={single.lastPt[1]}
            r={7}
          />
        ) : null}
        {drawn.map((d) => (
          <circle
            key={`${d.series.key}-now`}
            className="rc-chart__now"
            cx={d.lastPt[0]}
            cy={d.lastPt[1]}
            r={3.5}
            style={{ "--rc-chart-accent": d.color } as CSSProperties}
          />
        ))}
        {single ? (
          <text
            className="rc-chart__peak"
            x={Math.min(single.lastPt[0] - 8, W - 20)}
            y={Math.max(single.lastPt[1] - 10, 14)}
            textAnchor="end"
          >
            {formatValue(single.last)}
          </text>
        ) : null}
        <text className="rc-chart__axis" x={2} y={H - 4}>
          {formatBucket(buckets[0] ?? "", 0)}
        </text>
        <text className="rc-chart__axis" x={W - 2} y={H - 4} textAnchor="end">
          {axisEndLabel}
        </text>
      </svg>
      {hoverTooltipNode}
    </figure>
  );
}
